/* eslint-disable max-len */
/* eslint-disable max-lines */
import {
	turnSelectors,
	carSelectors, productsSelectors,
	financialStatementsSelectors,
	employeesSelectors,
	logbookSelectors,
} from '~/store';
import {
	getSystemIncomeStatementParameters,
	createNewQuarter,
} from '~/store/financial-statements/actions/helpers';
import {
	getFinancialRatiosFor
} from '~/store/financial-statements/selectors';
import { TURN_FETCHING_NEXT } from '~/store/turn/actionTypes';
import { safeDivision, turnToLogbookDate } from '~/lib/helpers';
import { TURN_PHASES } from '~/lib/constants';
import { getIncomeStatementFor } from '../../../financial-statements/selectors';
import { getCurrentQuarterNum } from '../../../turn/selectors';
// ---- internal imports ----


function customMissionHandler(state, action, mission) {
	let hasMissionsChanged = false;

	switch (mission.customHandler) {
	case 'ACHIEVE_PROFIT':
		[mission, hasMissionsChanged] = achieveProfit(state, action, mission);
		break;

	case 'SUBMIT_PROFITABLE_FINANCIAL_STATEMENT':
		[mission, hasMissionsChanged] = submitProfitableFinancialStatement(action, mission);
		break;

	case 'DISTRIBUTE_PROFIT_AFTER_QUARTER':
		[mission, hasMissionsChanged] = distributeProfit(state, action, mission);
		break;

	case 'BREAKEVEN_ON_CARGO_CAR':
		[mission, hasMissionsChanged] = breakEvenOnCargoCar(state, action, mission);
		break;

	case 'SUBMIT_FINANCIAL_RATIOS':
		[mission, hasMissionsChanged] = submitFinancialRatios(state, action, mission);
		break;
	
	case 'SALES_WITH_NET_MARGIN':
		[mission, hasMissionsChanged] = salesWithNetMargin(state, action, mission);
		break;

	case 'SALES_REVENUE_IN_ONE_MONTH':
		[mission, hasMissionsChanged] = salesRevenueInOneMonth(state, action, mission);
		break;

	case 'ACHIEVE_QUARTERLY_NET_PROFIT_MARGIN':
		[mission, hasMissionsChanged] = achieveQuarterlyNetProfitMargin(state, action, mission);
		break;

	case 'IMPROVED_ASSET_TURNOVER_RATIO':
		[mission, hasMissionsChanged] = achieveImprovedRatio(state, action, mission, 'assetTurnoverRatio');
		break;

	case 'IMPROVED_INVENTORY_TURNOVER_RATIO':
		[mission, hasMissionsChanged] = achieveImprovedRatio(state, action, mission, 'inventoryTurnoverRatio');
		break;

	default:
		console.error('Invalid mission event: ' + mission.event);
		break;
	}

	return [mission, hasMissionsChanged];
}

function achieveProfit(state, action, mission) {
	const { startedInTurn } = mission;
	const currentTurn = turnSelectors.getTurnNum(state);
	if (currentTurn - startedInTurn <= 1) {
		//	level 3 and 4 have the same trigger event
		//	this condition is here to prevent jumping
		//	from level 2 to 4 directly
		return [mission, false];
	}

	const { didQuarterEnd, financialStatements } = action;

	if (!didQuarterEnd) {
		return [mission, false];
	}

	const prevQuarterNum = turnSelectors.getCurrentQuarterNum(state) - 1;
	const isSubmitted = financialStatements.isSubmittedByQuarterNum[prevQuarterNum];

	if (!isSubmitted) {
		return [mission, false];
	}

	const prevQuarterIncomeStatement = financialStatements.systemStatementsByQuarterNum[prevQuarterNum];
	const netProfit = prevQuarterIncomeStatement?.incomeStatement?.netProfit ?? 0;
	mission.value = netProfit;

	if (netProfit >= mission.target) {
		mission.completed = true;
	}

	mission.hasEvaluatedMission = true;
	return [mission, true];
}

function submitProfitableFinancialStatement(action, mission) {
	const { isSubmitted = false, financialStatements = {} } = action;
	const netProfit = financialStatements?.incomeStatement?.netProfit ?? 0;
	if (!isSubmitted) {
		return [mission, false];
	}

	mission.value = netProfit;
	if (netProfit >= mission.target) {
		mission.completed = true;
	}
	mission.hasEvaluatedMission = true;

	return [mission, true];
}

function distributeProfit(state, action, mission) {
	const { startedInTurn } = mission;
	const currentTurn = turnSelectors.getTurnNum(state);
	if (currentTurn - startedInTurn < 3) {
		//	the mission states that one quarter should
		//	pass, then check the user distribution after the
		//	oen quarter is done.
		return [mission, false];
	}

	if (action.type === 'financialStatements.DISTRIBUTE_PROFIT') {
		mission.hasEvaluatedMission = true;
		if (action.amount >= mission.target) {
			mission.completed = true;
		}
		return [mission, true];
	}
	if (action.type === 'turn.FETCHING_NEXT') {

		const currentRetainedEarnings = financialStatementsSelectors.getCurrentRetainedEarnings(state);

		const shouldAttemptToDistributeProfit = currentRetainedEarnings > 0;

		if (shouldAttemptToDistributeProfit) {
			return [mission, false];
		}
		else {
			mission.hasEvaluatedMission = true;
			return [mission, true];
		}
	}
}

function breakEvenOnCargoCar(state, action, mission) {
	const turnNum = turnSelectors.getTurnNum(state);
	const turnPhase = turnSelectors.getTurnPhase(state);
	const shallAssessMission = 
		TURN_PHASES.CUSTOMERS === turnPhase && 
		(mission.checkAtStartOfTurn - turnNum) === 1;
	if (!shallAssessMission) {
		return [mission, false];
	}
	const leasedCars = carSelectors.getLeasedCarsSinceTurnNumberInclusive(state, mission.startedInTurn);

	if (leasedCars.length === 0) {
		mission.value = 0;
		mission.target = 0;
		return [mission, false];
	}

	const characterPurchasePrice = productsSelectors.getCharacterPurchasePrice(state);
	let totalRentPaidForCars = 0;
	let totalSavingFromCars = 0;

	leasedCars.forEach((car) => {
		let carSaving = 0;
		let totalCarCost = 0;
		let { startTurn, endTurn, discountPercent } = car;
		if (!endTurn) {
			endTurn = turnSelectors.getTurnNum(state);
		}
		const discountFactor = parseFloat(discountPercent) ;
		const carPurchasingPrice = {};

		Object.keys(characterPurchasePrice)
			.forEach((productType) => {
				carPurchasingPrice[productType] = Math.round(characterPurchasePrice[productType] * (1 - discountFactor));
			});

		let rentingTurn;
		for ( rentingTurn = startTurn; rentingTurn <= endTurn; rentingTurn++) {
			totalCarCost += car.price;
			const logbookDate = turnToLogbookDate(rentingTurn);

			const purchaseLogs = logbookSelectors.getSystemPurchaseLogFromDateStr(state, logbookDate);
			
			Object.keys(carPurchasingPrice)
				.forEach((productType) => {
					const loggedProduct = purchaseLogs[productType]
						.filter((log) => log.cost === carPurchasingPrice[productType]);

					if (loggedProduct.length > 0) {
						carSaving += (characterPurchasePrice[productType] - loggedProduct[0].cost) * loggedProduct[0].amount;
					}
				});
		}

		totalRentPaidForCars += totalCarCost;
		totalSavingFromCars += carSaving;
	});

	mission.value = totalSavingFromCars;
	mission.target = totalRentPaidForCars;

	if (totalSavingFromCars >= totalRentPaidForCars) {
		mission.completed = true;
		mission.saving = totalSavingFromCars;
	}

	return [mission, true];
}

function submitFinancialRatios(state, action, mission) {
	const { quarterNum, isSubmitted } = action;
	if(!isSubmitted) {
		return [mission, false];
	}
	
	const isQuarterAfterLevelStartingTurn = quarterNum * 3 > mission.startedInTurn;
	const financialRatios =  financialStatementsSelectors.getFinancialRatiosFor(state, quarterNum);
	const isFsRatiosExist = financialRatios && financialRatios.returnOnAssets;

	/**
	 * if financialRatios exists, and isSubmitted consider
	 * financial ratios has been submitted 
	 */
	if (isQuarterAfterLevelStartingTurn && isFsRatiosExist) {
		mission.completed = true;
	}

	return [mission, mission.completed];
}

function salesWithNetMargin(state, action, mission) {
	const { value: cashUpdateVal = 0 } = action;
	const val = Math.max(0, cashUpdateVal);

	if (val === 0) {
		return [mission, false];
	}

	mission.value += val;

	const quarter = createNewQuarter(turnSelectors.getCurrentQuarterNum(state));
	const { netProfit, revenue } = getSystemIncomeStatementParameters(state, quarter);
	const netProfitMargin = (netProfit / revenue) * 100;

	if (revenue >= mission.target && netProfitMargin > 15) {
		mission.completed = true;
	}
	
	return [mission, true];
}

function salesRevenueInOneMonth(state, action, mission) {
	const { type: actionType } = action;
	if(actionType === TURN_FETCHING_NEXT) {
		const isPreparationPhase = action.previousPhase === TURN_PHASES.CUSTOMERS;
		if(isPreparationPhase && !mission.completed) {
			// reset value to re-start recording for current month
			mission.value = 0;
			return [mission, true];
		}
	}

	const revenueUpdateValue = action.value ?? 0;
	mission.value += Math.max(0, revenueUpdateValue);

	if(mission.value >= mission.target) {
		mission.completed = true;
	}

	return [mission, revenueUpdateValue > 0];
}

function achieveQuarterlyNetProfitMargin(state, action, mission) {
	const turnNum = turnSelectors.getTurnNum(state);

	//	one quarter is 3 month, missions are assessed at end of quarter
	//	however some levels can start after new quarter started
	//	so this makes the actual time of this level 2 turns not 3
	const hasOneQuarterPassed =  turnNum - mission.startedInTurn >= 2;
	if (!hasOneQuarterPassed || !action.isSubmitted) {
		return [mission, false];
	}

	const financialRatios = action.financialRatios;
	const { revenue, netProfit } = financialRatios;

	mission.hasEvaluatedMission = true;

	if (revenue === 0 || netProfit <= 0) {
		return [mission, true];
	}

	const netProfitMargin =  (netProfit / revenue) * 100;

	if (netProfitMargin >= (mission.target)) {
		mission.completed = true;
		return [mission, true];
	}
	else {
		mission.value = netProfitMargin;
		return [mission, true];
	}
}

function achieveImprovedRatio(state, action, mission, ratio) {
	const turnNum = turnSelectors.getTurnNum(state);

	//	one quarter is 3 month, missions are assessed at end of quarter
	//	however some levels can start after new quarter started
	//	so this makes the actual time of this level 2 turns not 3
	const hasOneQuarterPassed =  turnNum - mission.startedInTurn >= 2;
	if (!hasOneQuarterPassed || !action.isSubmitted) {
		return [mission, false];
	}

	const { financialRatios, quarterNum } = action;

	const ratioValue = financialRatios[ratio];

	if (!financialRatios) {
		mission.hasEvaluatedMission = true;
		return[mission, true];
	}

	const prevQuarterFinancialRatios = getFinancialRatiosFor(state, quarterNum - 1);

	let prevQuarterRatioValue = (quarterNum === 1)
		? 0
		: prevQuarterFinancialRatios[ratio];

	const percentageDifference = ((ratioValue - prevQuarterRatioValue) / prevQuarterRatioValue) * 100;

	if (percentageDifference >= mission.target) {
		mission.completed = true;
	}
	else {
		mission.hasEvaluatedMission = true;
	}
	return [mission, true];
}

export default customMissionHandler;
